import { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { getMediaList, getProblems, toggleStar } from '../utils/api';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Input from '@mui/material/Input';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { deepOrange, deepPurple, green } from '@mui/material/colors';
import ImageCarousel from './image-carousel';
import Loading from './loading';
import ProblemViewer from './problem-viewer';
import TickModal from './tick-modal';
import TickListModal from './tick-list-modal';

const getGradeColor = (grade) => {
  if (!grade) return undefined;
  if (grade.startsWith('6')) return deepOrange[500];
  if (grade.startsWith('7')) return deepPurple[500];
  if (grade.startsWith('8')) return green[500];
  return undefined;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};

const ProblemList = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [_, setForceRefresh] = useState(null);
  const [problems, setProblems] = useState(null);
  const [showProblem, setShowProblem] = useState(null);

  const [visibleGrades, setVisibleGrades] = useLocalStorage('visibleGrades', [
    {grade: "8B", enable: true},{grade: "8A+", enable: true},{grade: "8A", enable: true},
    {grade: "7C+", enable: true},{grade: "7C", enable: true},{grade: "7B+", enable: true},{grade: "7B", enable: true},{grade: "7A+", enable: true},{grade: "7A", enable: true},
    {grade: "6C+", enable: true},{grade: "6C", enable: true},{grade: "6B+", enable: true},{grade: "6B", enable: true},{grade: "6A+", enable: true},{grade: "6A", enable: true}
  ]);

  const [visibleTypes, setVisibleTypes] = useLocalStorage('visibleType', [{type: "Armgang", enable: true},{type: "Catch", enable: true},{type: "Fingerjugs", enable: true},{type: "Gode grep", enable: true},{type: "Klemming", enable: true},{type: "Krimp", enable: true},{type: "Lett blanding", enable: true},
    {type: "Pinsj", enable: true},{type: "Skulderflytt", enable: true},{type: "Sloper", enable: true},{type: "Store flytt", enable: true}
  ]);
  const [hideDone, setHideDone] = useLocalStorage('hideDone', true);
  const [onlyStarred, setOnlyStarred] = useLocalStorage('onlyStarred', false);
  const [showTickModal, setShowTickModal] = useState(false);
  const [showTickListModal, setShowTickListModal] = useState(false);
  const [visibleMedia, setVisibleMedia] = useLocalStorage('visibleMedia', null);
  
  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    }
  }, [getAccessTokenSilently, isAuthenticated]);
  
  useEffect(() => {
    getProblems(accessToken, 0)
    .then((data) => setProblems(data))
    .catch((error) => {
      console.warn(error);
      alert(error.toString());
    });
    getMediaList(accessToken)
    .then((data) => {
      if (data) {
        let vm = data.filter(m => !m.hidden && !m.carouselMedia);
        vm.forEach(m => {
          let localStorageMedia = visibleMedia && visibleMedia.filter(x => x.id === m.id);
          if (localStorageMedia && localStorageMedia.length > 0) {
            m.enable = localStorageMedia[0].enable;
          } else {
            m.enable = true;
          }
        });
        setVisibleMedia(vm);
      }
    })
    .catch((error) => {
      console.warn(error);
      alert(error.toString());
    });
  }, [accessToken]);

  const navigate = (direction) => {
    const problemsLst = problems.filter(p =>
      visibleMedia.filter(x => x.id === p.mediaId && x.enable).length>0 &&
      visibleGrades.filter(x => x.grade === p.grade && x.enable).length>0 &&
      visibleTypes.filter(x => x.type === p.type && x.enable).length>0 &&
      (!hideDone || (!p.ticks || p.ticks.filter(x => x.isMine).length==0)) &&
      (!onlyStarred || problems.filter(p => p.star).length==0 || p.star));
    var ix = -1;
    for (var i = 0; i < problemsLst.length; i++) {
      if (problemsLst[i].id === showProblem.id) {
        ix = i + direction;
        break;
      }
    }
    if (ix < 0) {
      ix = problemsLst.length-1;
    } else if (ix > problemsLst.length-1) {
      ix = 0;
    }
    setShowProblem(problemsLst[ix]);
  }

  const handleUserKeyPress = useCallback(event => {
    const { keyCode } = event;
    if (showProblem) {
      if (keyCode === 27) {
        setShowProblem(null);
      } else if (keyCode === 37) {
        navigate(-1);
      } else if (keyCode === 39) {
        navigate(1);
      }
    }
  }, [showProblem]);

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  if (isLoading || !problems || !visibleMedia) {
    return <Loading/>;
  }

  return (
    <span>
      <TickModal
        open={showTickModal}
        p={showProblem}
        onClose={(p) => {
          setShowTickModal(false);
          setShowProblem(p);
          var ix = -1;
          for (var i = 0; i < problems.length; i++) {
            if (problems[i].id === p.id) {
              ix = i;
              break;
            }
          }
          if (ix != -1) {
            problems.splice(ix, 1);
          }
          if (!p.deleted) {
            const pos = ix == -1? 0 : ix;
            problems.splice(pos, 0, p);
          }
          setProblems([...problems]);
          setShowProblem(p);
        }}
        accessToken={accessToken}
        isAuthenticated={isAuthenticated}
        loginWithRedirect={loginWithRedirect}
      />
      <TickListModal
        open={showTickListModal}
        p={showProblem}
        onClose={() => setShowTickListModal(false)}/>
      {showProblem &&
        <ProblemViewer
          p={showProblem}
          openTickModal={() => setShowTickModal(true)}
          openTickListModal={() => setShowTickListModal(true)}
          toggleStar={() => {
            toggleStar(accessToken, showProblem.id)
            .then(() => {
              setShowProblem(null);
              let p = problems.filter(x => x.id === showProblem.id)[0];
              p.star = !p.star;
              setProblems([...problems]);
              setShowProblem(p);
            })
            .catch((error) => {
              console.warn(error);
              alert(error.toString());
            });
          }}
          closeShowProblem={() => setShowProblem(null)}
          navigate={navigate}
          accessToken={accessToken}
          isAuthenticated={isAuthenticated}
          loginWithRedirect={loginWithRedirect}
          />
      }
      <Box sx={{ overflow: 'auto' }}>
        <ImageCarousel accessToken={accessToken}/>
        <FormGroup row>
          <Select style={{margin: 10}} MenuProps={MenuProps} multiple value={visibleMedia.filter(m => m.enable).map(m => m.id)} input={<Input/>} renderValue={(selected: any) => "Wall (" + selected.length + "/" + visibleMedia.length + ")"}
            onChange={e => {
              let value: any = e.target.value;
              let x = [...visibleMedia]; 
              if (value.includes(-1)) {
                const enable = x.filter(m => !m.enable).length>0;
                x.forEach(m => m.enable=enable);
              }
              else {
                x.forEach(m => m.enable=value.includes(m.id));
              }
              setForceRefresh(Math.random());
              setVisibleMedia(x);
            }}>
            {visibleMedia.map((m, key) => (
              <MenuItem key={key} value={m.id}>
                <Checkbox checked={m.enable} />
                <ListItemText primary={m.name} />
              </MenuItem>
            ))}
            <Divider/>
            <MenuItem key={-1} value={-1}>
              <Checkbox checked={visibleMedia.filter(x => !x.enable).length===0} />
              <ListItemText primary="Select all" />
            </MenuItem>
          </Select>
          <Select style={{margin: 10}} MenuProps={MenuProps} multiple value={visibleTypes.filter(t => t.enable).map(t => t.type)} input={<Input/>} renderValue={(selected: any) => "Type (" + selected.length + "/" + visibleTypes.length + ")"}
             onChange={e => {
               let value: any = e.target.value;
               let x = [...visibleTypes];
               if (value.includes(-1)) {
                 const enable = x.filter(t => !t.enable).length>0;
                 x.forEach(t => t.enable=enable);
               } else {
                 x.forEach(t => t.enable=value.includes(t.type));
               }
               setForceRefresh(Math.random());
               setVisibleTypes(x);
           }}>
             {visibleTypes.map((t, key) => (
               <MenuItem key={key} value={t.type}>
                 <Checkbox checked={t.enable} />
                 <ListItemText primary={t.type} />
               </MenuItem>
             ))}
             <Divider/>
             <MenuItem key={-1} value={-1}>
               <Checkbox checked={visibleTypes.filter(t => !t.enable).length===0} />
               <ListItemText primary="Select all" />
             </MenuItem>
           </Select>

           <Select style={{margin: 10}} MenuProps={MenuProps} multiple value={visibleGrades.filter(g => g.enable).map(g => g.grade)} input={<Input/>} renderValue={(selected: any) => "Grade (" + selected.length + "/" + visibleGrades.length + ")"}
             onChange={e => {
               let value: any = e.target.value;
               let x = [...visibleGrades];
               if (value.includes(-1)) {
                 const enable = x.filter(g => !g.enable).length>0;
                 x.forEach(g => g.enable=enable);
               } else {
                 x.forEach(g => g.enable=value.includes(g.grade));
               }
               setForceRefresh(Math.random());
               setVisibleGrades(x);
           }}>
             {visibleGrades.map((g, key) => (
               <MenuItem key={key} value={g.grade}>
                 <Checkbox checked={g.enable} />
                 <ListItemText primary={g.grade} />
               </MenuItem>
             ))}
             <Divider/>
             <MenuItem key={-1} value={-1}>
               <Checkbox checked={visibleGrades.filter(g => !g.enable).length===0} />
               <ListItemText primary="Select all" />
             </MenuItem>
           </Select>

          {problems.filter(p => p.ticks && p.ticks.filter(x => x.isMine).length>0).length>0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideDone}
                  onChange={() => setHideDone(!hideDone)}
                />
              }
              label="Hide ticked problems"
            />
          )}
          {problems.filter(p => p.star).length>0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyStarred}
                  onChange={() => setOnlyStarred(!onlyStarred)}
                />
              }
              label="Only starred"
            />
          )}
        </FormGroup>

        <List dense={true}>
          {problems.map(p => {
            if (visibleMedia.filter(x => x.id === p.mediaId && x.enable).length>0 &&
                visibleGrades.filter(x => x.grade === p.grade && x.enable).length>0 &&
                visibleTypes.filter(x => x.type === p.type && x.enable).length>0 &&
                (!hideDone || (!p.ticks || p.ticks.filter(x => x.isMine).length==0)) &&
                (!onlyStarred || problems.filter(p => p.star).length==0 || p.star)) {
              var stars = "\u2606\u2606\u2606";
              if (p.ticks && p.ticks.length!=0) {
                var total = 0;
                p.ticks.forEach(t => total += t.stars);
                const avg = Math.round(total/p.ticks.length);
                if (avg===1.0) stars = "\u2605\u2606\u2606";
                else if (avg===2.0) stars = "\u2605\u2605\u2606";
                else if (avg===3.0) stars = "\u2605\u2605\u2605";
              }
              
              const avatarColor = getGradeColor(p.grade);

              return (
                <ListItem key={p.id} disablePadding>
                  <ListItemButton dense onClick={() => setShowProblem(p)}>
                    <Avatar 
                      sx={{ 
                         bgcolor: avatarColor, 
                         color: '#fff', 
                         marginRight: '10px' 
                      }} 
                      alt={p.grade}
                    >
                      {p.grade}
                    </Avatar>
                    <ListItemText
                      primary={`#${p.nr} ${p.type} (${visibleMedia.filter(x => x.id===p.mediaId)[0].name}) ${p.ticks.filter(x => x.isMine).length>0? '\u2713' : ''}`}
                      secondary={`${p.creator} | ${stars} ${p.ticks? p.ticks.length : 0} ascent(s)`} />
                  </ListItemButton>
                </ListItem>
              );
            }
          })}
        </List>
      </Box>
    </span>
  )
}

function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });

  const setValue = value => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.log(error);
    }
  };

  return [storedValue, setValue];
}

export default ProblemList;